/** Typed error with HTTP status for chat request failures. */
export class ChatError extends Error {
  readonly httpStatus: number;
  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = 'ChatError';
    this.httpStatus = httpStatus;
  }
}

export const OPTIBOT_ERROR_TYPE = {
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN',
} as const;

export type OptiBotErrorType = (typeof OPTIBOT_ERROR_TYPE)[keyof typeof OPTIBOT_ERROR_TYPE];

/** Classify a chat error into a known error type for user-facing messaging. */
export function classifyOptiBotError(error: Error): OptiBotErrorType {
  if (error.name === 'TimeoutError') return OPTIBOT_ERROR_TYPE.TIMEOUT;

  const httpStatus = error instanceof ChatError ? error.httpStatus : undefined;
  if (httpStatus === 401) return OPTIBOT_ERROR_TYPE.AUTH_EXPIRED;
  if (httpStatus === 429) return OPTIBOT_ERROR_TYPE.RATE_LIMITED;
  if (httpStatus !== undefined && httpStatus >= 500) return OPTIBOT_ERROR_TYPE.SERVER_ERROR;

  if (error instanceof TypeError) return OPTIBOT_ERROR_TYPE.NETWORK_ERROR;

  return OPTIBOT_ERROR_TYPE.UNKNOWN;
}

const ERROR_I18N_MAP: Record<OptiBotErrorType, string> = {
  [OPTIBOT_ERROR_TYPE.AUTH_EXPIRED]: 'optibot.error.authExpired',
  [OPTIBOT_ERROR_TYPE.RATE_LIMITED]: 'optibot.error.rateLimited',
  [OPTIBOT_ERROR_TYPE.SERVER_ERROR]: 'optibot.error.serverError',
  [OPTIBOT_ERROR_TYPE.NETWORK_ERROR]: 'optibot.error.networkError',
  [OPTIBOT_ERROR_TYPE.TIMEOUT]: 'optibot.error.timeout',
  [OPTIBOT_ERROR_TYPE.UNKNOWN]: 'optibot.error',
};

/** Map an error type to its i18n translation key. */
export function getErrorI18nKey(errorType: OptiBotErrorType): string {
  return ERROR_I18N_MAP[errorType];
}
